import {
  ChakraFlex as Flex,
  ChakraText as Text,
  ChakraButton as Button,
  ChakraLink as Link,
} from "@airhelp/react-ui-components";
import NextLink from "next/link";
import { SectionHeader } from "modules/common/components";
import type { ReactStoryblokComponent, ResolvedLinkProps } from "types/storyblok";
import { useStoryblokLink } from "hooks";
import { trackUAEvent, trackCustomEvent } from "utils/tracking";
import { useGoogleOptimizeExperimentId } from "contexts/DataContext/hooks";
import { useGoogleOptimize } from "hooks/useGoogleOptimize";

interface FeeOnHomepageProps {
  header: string;
  preheader: string;
  text: string;
  linkUrl: ResolvedLinkProps;
  linkText: string;
  ctaText: string;
  ctaUrl: ResolvedLinkProps;
}

const LINK_EVENT_UA = ["clicked", "FeeSection", "Link"] as const;
const LINK_EVENT = { event: "cta_click", label: "Link", placement: "FeeSection" } as const;

const CTA_EVENT_UA = ["clicked", "FeeSection", "CheckYourCompensation"] as const;
const CTA_EVENT = { event: "check_compensation", label: "check_your_compensation", placement: "fee_section" } as const;

const EXPERIMENT_NAME = "fee-on-homepage";

export const FeeOnHomepage = ({
  blok: { preheader, header, text, linkText, linkUrl, ctaUrl, ctaText },
}: ReactStoryblokComponent<FeeOnHomepageProps>) => {
  const resolvedLink = useStoryblokLink(linkUrl);
  const resolvedCtaLink = useStoryblokLink(ctaUrl);
  const experimentId = useGoogleOptimizeExperimentId(EXPERIMENT_NAME, [
    { language: "en-nz" },
    { language: "en" },
    { language: "en-gb" },
    { language: "en-ie" },
    { language: "en-ca" },
    { language: "en-au" },
    { language: "en-in" },
  ]);
  const experimentVariant = useGoogleOptimize(experimentId);

  const handletrackEventsLink = () => {
    trackUAEvent(LINK_EVENT_UA);
    trackCustomEvent(LINK_EVENT);
  };

  const handletrackEventsCta = () => {
    trackUAEvent(CTA_EVENT_UA);
    trackCustomEvent(CTA_EVENT);
  };

  return (
    <Flex direction="column" maxW={{ base: "none", md: "470px" }}>
      <SectionHeader
        preheader={preheader}
        header={header}
        gap={2}
        mr={{ base: "initial", xl: 8 }}
        alignItems="flex-start"
      />
      <Text fontSize={{ base: "md", md: "lg" }} mt={6} color="primary.700">
        {text}{" "}
        {linkUrl && (
          <Link as={NextLink} href={resolvedLink} onClick={handletrackEventsLink} fontSize="md">
            {linkText}
          </Link>
        )}
      </Text>
      {Number(experimentVariant) === 2 && (
        <Button
          as={NextLink}
          href={resolvedCtaLink}
          onClick={handletrackEventsCta}
          variant="primary"
          size={{ base: "m", lg: "l" }}
          mt={{ base: 8, md: 10 }}
          width={{ base: "100%", md: "auto" }}
          maxW={{ base: "none", md: "388px" }}
        >
          {ctaText}
        </Button>
      )}
    </Flex>
  );
};
